// UI copy is localized via the account:member_modal.* / account:manage_members.* / account:roles.* catalogs (6 locales).
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import ToggleButton from "components/ToggleButton";
import Tooltip from "components/Tooltip";
import ProgressBar from "components/ProgressBar";
import { IconClose, IconHelp, IconWarning } from "icons";
import { lockBodyScroll, unlockBodyScroll } from "utils/bodyScrollLock";
import teamsService from "api/teams/teams.service";
import { IMemberPermissionsResponse, ITeamCreditUsageMember, MemberPermissionOverride } from "api/teams/teams.type";
import { useSnackbar } from "hooks/useSnackbar";
import { AnalyticsEvent } from "utils";
import { getReasonCode, reasonErrorText } from "utils/reasonError";

// Info-icon tooltip explaining a credit number. `tip` is already translated by the caller.
const InfoTip = ({ tip }: { tip: string }) => (
  <Tooltip content={tip} position="top" contentClassName="!whitespace-normal max-w-[200px] leading-snug">
    <IconHelp className="w-3.5 h-3.5 text-grey cursor-help" />
  </Tooltip>
);

// REF-5611: "No limit" pill for unlimited members. Not "unlimited" as if infinite — the member
// still spends from the pool remainder; the real cap/spent numbers sit alongside the badge.
const NoLimitBadge = () => {
  const { t } = useTranslation();

  return <span className="text-bodySm px-2 py-0.5 rounded whitespace-nowrap w-fit bg-purple/20 text-purple dark:text-green dark:bg-green/20">{t("account:manage_members.no_limit")}</span>;
};

interface ManageMemberModalProps {
  member: ITeamCreditUsageMember;
  pool: number;
  unassigned: number;
  // REF-5611: team-wide no-limit switch state. When on, per-member allocation is logically
  // disabled (everyone shares the pool) — the editor is replaced by a note.
  membersUnlimited: boolean;
  // Pre-formatted plan end date (the team plan's package_detail.end_date), shown as credit validity.
  planActiveUntil?: string | null;
  // Roster profile photo (the credit-usage member shape carries no photo) — falls back to an initial.
  profilePhoto?: string | null;
  // Opens the parent's remove-member confirmation. Omitted for the current user (can't remove self).
  onRemove?: () => void;
  onClose: () => void;
  onSaved: () => void;
}

// REF-5549 (BE REF-5446): a right-side "Manage member" drawer with two independently-saved sections.
//  - Credits: an ABSOLUTE refabric cap (Σ allocations <= pool, BE-enforced; 409 surfaces would_be_total),
//    edited with a slider (hero) + an exact number input. The slider's max is this member's reachable
//    ceiling without conflict = current allocation + the unassigned remainder.
//  - Permissions: per-module toggles bounded by team_max; ON grants the ceiling, OFF hides the module
//    (both flags false). Hidden entirely for Admin rows — the BE refuses to narrow an admin.
const ManageMemberModal: React.FC<ManageMemberModalProps> = ({ member, pool, unassigned, membersUnlimited, planActiveUntil, profilePhoto, onRemove, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const isAdmin = member.role === "Admin";
  const roleLabel = isAdmin ? t("account:roles.admin") : t("account:roles.standard_user");

  // Drawer slide-in/out: mount off-screen (translate-x-full), flip `entered` on the next tick so the
  // transition runs; on close, slide back out then unmount via the parent after the transition.
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    lockBodyScroll();
    const id = setTimeout(() => setEntered(true), 10);

    return () => {
      clearTimeout(id);
      unlockBodyScroll();
    };
  }, []);

  const handleClose = useCallback(() => {
    setEntered(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  // Edit the SETTABLE absolute (allocated_refabric), not the effective cap. For a member with no
  // explicit row, allocated_refabric is null → fall back to cap (the equal default) as the start
  // value. (For admins cap = own + unassigned remainder, so cap is never the editable value.)
  const initialAllocation = member.allocated_refabric ?? member.cap;
  const [allocation, setAllocation] = useState<number>(initialAllocation);
  // REF-5611: per-member no-limit flag. When on, the fixed cap input is disabled and the save body
  // is { is_unlimited: true } (allocated_refabric ignored).
  const initialIsUnlimited = member.is_unlimited ?? false;
  const [isUnlimited, setIsUnlimited] = useState<boolean>(initialIsUnlimited);
  const [savingCredit, setSavingCredit] = useState(false);
  // Localized inline error for the credit save (resolved from the BE reason_code). Distinguishes
  // "exceeds pool" from "below already-spent" — the same 409 was previously mislabeled as the former.
  const [creditError, setCreditError] = useState<string | null>(null);
  const [editingAllocation, setEditingAllocation] = useState(false);

  // Slider ceiling = the most this member can hold without pushing the team total over the pool
  // (their current allocation + everything currently unassigned). Step keeps ~100 stops on the track.
  const sliderMax = Math.max(1, initialAllocation + unassigned);
  const sliderStep = Math.max(1, Math.round(sliderMax / 100));
  const sliderValue = Math.min(allocation, sliderMax);
  const fillPct = sliderMax > 0 ? Math.min(100, (sliderValue / sliderMax) * 100) : 0;

  // Save is enabled when the no-limit flag flipped, or (while fixed) the allocation changed.
  const creditDirty = isUnlimited !== initialIsUnlimited || (!isUnlimited && allocation !== initialAllocation);

  const [permData, setPermData] = useState<IMemberPermissionsResponse | null>(null);
  const [permLoading, setPermLoading] = useState(!isAdmin);
  const [toggles, setToggles] = useState<Record<string, boolean>>({});
  // Baseline the toggles were loaded/last-saved at — drives the dirty check for the Save button.
  const [savedToggles, setSavedToggles] = useState<Record<string, boolean>>({});
  const [savingPerm, setSavingPerm] = useState(false);

  useEffect(() => {
    if (isAdmin) return;

    let cancelled = false;

    teamsService
      .getMemberPermissions(member.user_id)
      .then((res) => {
        if (cancelled || res?._handled || !res.responseData) return;

        setPermData(res.responseData);
        const init: Record<string, boolean> = {};
        Object.keys(res.responseData.team_max ?? {}).forEach((key) => {
          init[key] = res.responseData.effective?.[key as keyof typeof res.responseData.effective]?.is_enabled === true;
        });
        setToggles(init);
        setSavedToggles(init);
      })
      .catch(() => {
        if (!cancelled) showError(t("account:member_modal.errors.load_permissions"));
      })
      .finally(() => {
        if (!cancelled) setPermLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [member.user_id, isAdmin, showError, t]);

  const handleSaveCredit = async () => {
    setSavingCredit(true);
    setCreditError(null);

    try {
      // No-limit ignores allocated_refabric (BE); a fixed cap sends the absolute value.
      const res = await teamsService.setMemberCreditShare(member.user_id, isUnlimited ? { is_unlimited: true } : { allocated_refabric: allocation, is_unlimited: false });

      if (res?._handled) return;

      AnalyticsEvent("member_credit_allocation_saved", "", "success", { user_id: member.user_id, allocated: isUnlimited ? null : allocation, is_unlimited: isUnlimited });
      showSuccess(t("account:member_modal.toasts.credit_saved", { name: member.name || member.email }));
      onSaved();
      setEditingAllocation(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const code = getReasonCode(err);
      const rd = err?.response?.data?.responseData;

      // Branch on reason_code (REF-5446). The two distinct 409s were previously collapsed into the
      // pool message; allocation_below_spent now gets its own text with the already-spent amount.
      // Allocation problems are inline (next to the input); admin/membership errors are a snackbar.
      if (code === "allocation_below_spent") {
        setCreditError(
          reasonErrorText(err, t("account:member_modal.errors.below_spent"), {
            spent: (rd?.spent ?? member.spent).toLocaleString(),
            requested: (rd?.requested ?? allocation).toLocaleString(),
          })
        );
      } else if (code === "allocation_exceeds_pool" || status === 409) {
        setCreditError(
          reasonErrorText(err, t("account:member_modal.errors.exceeds_pool"), {
            would_be_total: (rd?.would_be_total ?? 0).toLocaleString(),
            pool: (rd?.pool ?? pool).toLocaleString(),
          })
        );
      } else if (code === "invalid_allocation" || code === "allocation_negative" || status === 400) {
        setCreditError(reasonErrorText(err, t("account:member_modal.errors.invalid_allocation")));
      } else if (code === "admin_required" || status === 403) {
        showError(reasonErrorText(err, t("account:member_modal.errors.admin_only")));
      } else if (code === "not_team_member" || status === 404) {
        showError(reasonErrorText(err, t("account:member_modal.errors.not_team_member")));
      } else {
        showError(reasonErrorText(err, t("account:member_modal.errors.credit_generic")));
      }
    } finally {
      setSavingCredit(false);
    }
  };

  const handleSavePerms = async () => {
    if (!permData) return;

    setSavingPerm(true);

    const permissions: MemberPermissionOverride = {};
    Object.keys(permData.team_max).forEach((key) => {
      const max = permData.team_max[key as keyof typeof permData.team_max];
      // ON grants up to the team ceiling; OFF hides the module entirely. Capping at team_max
      // guarantees the override only ever narrows, so the BE never rejects with 400.
      permissions[key as keyof MemberPermissionOverride] = toggles[key] ? { is_enabled: max?.is_enabled ?? false, is_visible: max?.is_visible ?? false } : { is_enabled: false, is_visible: false };
    });

    try {
      const res = await teamsService.setMemberPermissions(member.user_id, { permissions });

      if (res?._handled) return;

      AnalyticsEvent("member_permissions_saved", "", "success", { user_id: member.user_id });
      showSuccess(t("account:member_modal.toasts.permissions_saved", { name: member.name || member.email }));
      setSavedToggles(toggles);
      onSaved();
    } catch (err: any) {
      const code = getReasonCode(err);

      if (code === "admin_perms_locked") showError(reasonErrorText(err, t("account:member_modal.errors.admin_perms_locked")));
      else if (code === "permission_exceeds_max" || code === "invalid_permissions" || code === "unknown_permission_node")
        showError(reasonErrorText(err, t("account:member_modal.errors.perms_exceed")));
      else if (code === "not_team_member") showError(reasonErrorText(err, t("account:member_modal.errors.not_team_member")));
      else showError(reasonErrorText(err, t("account:member_modal.errors.perms_generic")));
    } finally {
      setSavingPerm(false);
    }
  };

  const permKeys = permData ? Object.keys(permData.team_max) : [];
  // Only surface the permissions Save button once a toggle differs from the loaded/last-saved set.
  const permsDirty = permKeys.some((key) => (toggles[key] ?? false) !== (savedToggles[key] ?? false));

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      {/* Scrim — always-dark (bg-trueBlack), matching the app's modal backdrop. */}
      <div className={clsx("absolute inset-0 bg-trueBlack/50 transition-opacity duration-200", entered ? "opacity-100" : "opacity-0")} onClick={handleClose} role="presentation" />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("account:member_modal.aria_manage", { name: member.name || member.email })}
        className={clsx(
          "absolute right-0 top-0 h-full w-[460px] max-w-[92vw] flex flex-col bg-black border-l border-black-300 shadow-2xl transition-transform duration-200 ease-out",
          entered ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header — avatar + name + role badge + email + close */}
        <div className="flex items-center gap-3 p-4 bg-body-100 border-b border-black-300 flex-shrink-0">
          <div className="flex-center rounded-full bg-primary w-12 h-12 text-h6 text-primary-foreground uppercase overflow-hidden flex-shrink-0">
            {profilePhoto ? <img src={profilePhoto} alt={member.name} className="w-full h-full object-cover" /> : member.name?.[0] ?? member.email?.[0] ?? "?"}
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-h6 text-trueBlack dark:text-white truncate">{member.name || member.email}</span>
            <span className="text-bodySm text-grey truncate">{member.email}</span>
          </div>
          <button
            type="button"
            aria-label={t("account:member_modal.close")}
            className="flex-center w-8 h-8 rounded-md text-grey hover:text-trueBlack dark:hover:text-white hover:bg-black-300/60 flex-shrink-0"
            onClick={handleClose}
          >
            <IconClose className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex flex-col gap-3 p-4 flex-1 overflow-y-auto no-scrollbar">
          {/* Seat type */}
          <div className="flex flex-col gap-2 rounded-md border border-black-300 bg-body-100 p-4">
            <span className="text-h7 text-trueBlack dark:text-white">{t("account:member_modal.seat_type")}</span>
            <span className={clsx("text-bodySm px-2 py-0.5 rounded whitespace-nowrap w-fit", isAdmin ? "bg-purple/20 text-purple dark:text-green dark:bg-green/20" : "bg-black-300 text-grey")}>
              {roleLabel}
            </span>
            {planActiveUntil && <span className="text-bodySm text-grey">{t("account:member_modal.plan_active_until", { date: planActiveUntil })}</span>}
          </div>

          {/* Credit allocation */}
          <div className="flex flex-col gap-3 rounded-md border border-black-300 bg-body-100 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-h7 text-trueBlack dark:text-white">{t("account:member_modal.credit_allocation")}</span>
              {!isAdmin && !editingAllocation && !membersUnlimited && (
                <button type="button" className="text-bodySm text-purple dark:text-green hover:underline" onClick={() => setEditingAllocation(true)}>
                  {t("account:member_modal.edit_allocation")}
                </button>
              )}
            </div>

            {isAdmin ? (
              // An admin's cap = own allocation + the unassigned team remainder, so it can't be set
              // directly (any value entered is cancelled out by the remainder). Show it read-only.
              <div className="flex flex-col gap-1.5">
                <NoLimitBadge />
                <span className="text-h4 text-trueBlack dark:text-white">{member.cap.toLocaleString()}</span>
                <span className="text-bodySm text-grey">{t("account:member_modal.admin_note")}</span>
              </div>
            ) : membersUnlimited ? (
              // REF-5611: team-wide no-limit is on — per-member allocation is disabled; everyone shares the pool.
              <div className="flex flex-col gap-1.5">
                <NoLimitBadge />
                <span className="text-bodySm text-grey">{t("account:member_modal.team_wide_note", { spent: member.spent.toLocaleString() })}</span>
              </div>
            ) : !editingAllocation ? (
              member.is_unlimited ? (
                // REF-5611: per-member no-limit — draws from the team's unassigned remainder (like an admin).
                <div className="flex flex-col gap-1.5">
                  <NoLimitBadge />
                  <span className="text-bodySm text-grey">{t("account:member_modal.per_member_note", { spent: member.spent.toLocaleString(), cap: member.cap.toLocaleString() })}</span>
                </div>
              ) : (
                // View — how much of the allocated cap this member has spent this period.
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-bodySm text-grey">{t("account:member_modal.used_this_period")}</span>
                    <span className="text-bodyMd text-trueBlack dark:text-white">
                      {member.spent.toLocaleString()} / {member.cap.toLocaleString()}
                    </span>
                  </div>
                  <ProgressBar maxAmount={member.cap} currentAmount={member.spent} progressBarClassName="!bg-purple dark:!bg-green" trackClassName="bg-body-200" />
                </div>
              )
            ) : (
              // Edit — toggle No limit, or set the absolute cap with the slider or an exact value.
              <>
                {/* REF-5611 No-limit toggle — when on, the fixed-cap input is hidden and the save body is { is_unlimited: true }. */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:manage_members.no_limit")}</span>
                    <span className="text-bodySm text-grey">{t("account:member_modal.no_limit_hint")}</span>
                  </div>
                  <ToggleButton
                    isOn={isUnlimited}
                    onToggle={() => {
                      setIsUnlimited((v) => !v);
                      setCreditError(null);
                    }}
                    thumbClassName={isUnlimited ? "!bg-purple dark:!bg-green" : "!bg-grey"}
                    checkedTrackClassName="!bg-purple/20 dark:!bg-green/20"
                  />
                </div>

                {isUnlimited ? (
                  <span className="text-bodySm text-grey">{t("account:member_modal.no_limit_desc")}</span>
                ) : (
                  <>
                    <span className="text-h3 text-trueBlack dark:text-white">
                      {allocation.toLocaleString()} <span className="text-bodySm text-grey">{t("account:member_modal.credits_unit")}</span>
                    </span>

                    {/* Slider — green fill via inline gradient (Chrome track + thumb styled below). */}
                    <input
                      type="range"
                      min={0}
                      max={sliderMax}
                      step={sliderStep}
                      value={sliderValue}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setAllocation(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                        setCreditError(null);
                      }}
                      aria-label="Credit allocation slider"
                      className="mt-1 h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none [--slider-fill:#6266F0] dark:[--slider-fill:#D1EF70] [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-purple dark:[&::-moz-range-thumb]:border-green [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:bg-trueWhite [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-purple dark:[&::-webkit-slider-thumb]:border-green [&::-webkit-slider-thumb]:bg-trueWhite [&::-webkit-slider-thumb]:shadow"
                      style={{ background: `linear-gradient(to right, var(--slider-fill) ${fillPct}%, rgb(var(--th-black-300)) ${fillPct}%)` }}
                    />
                    <div className="flex items-center justify-between text-bodySm text-grey">
                      <span>0</span>
                      <span className="flex items-center gap-1">
                        {t("account:member_modal.max", { max: sliderMax.toLocaleString() })} <InfoTip tip={t("account:member_modal.max_tip")} />
                      </span>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-end gap-2.5">
                  {!isUnlimited && (
                    <input
                      type="number"
                      min={0}
                      value={allocation}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setAllocation(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                        setCreditError(null);
                      }}
                      aria-label="Credit allocation"
                      className="flex-1 h-10 bg-black-200 border border-black-300 text-trueBlack dark:text-white text-bodySm rounded-[5px] px-2.5 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  )}
                  <Button
                    className="btn-secondary-medium"
                    onClick={() => {
                      setAllocation(initialAllocation);
                      setIsUnlimited(initialIsUnlimited);
                      setCreditError(null);
                      setEditingAllocation(false);
                    }}
                  >
                    {t("account:member_modal.cancel")}
                  </Button>
                  <Button className="btn-primary-medium" onClick={handleSaveCredit} disabled={savingCredit || !creditDirty}>
                    {savingCredit ? t("account:member_modal.saving") : t("account:member_modal.save")}
                  </Button>
                </div>
                {creditError && (
                  <div className="flex items-start gap-2 p-2.5 text-bodySm text-orange bg-orange/10 border border-orange/30 rounded-md">
                    <IconWarning className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {creditError}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Feature access (permissions) */}
          {!isAdmin && (
            <div className="flex flex-col gap-3 rounded-md border border-black-300 bg-body-100 p-4">
              <span className="text-h7 text-trueBlack dark:text-white">{t("account:member_modal.feature_access")}</span>
              {permLoading ? (
                <span className="text-bodySm text-grey">{t("account:member_modal.loading_permissions")}</span>
              ) : permKeys.length === 0 ? (
                <span className="text-bodySm text-grey">{t("account:member_modal.no_features")}</span>
              ) : (
                <>
                  <div className="flex flex-col gap-2.5">
                    {permKeys.map((key) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-bodyMd text-trueBlack dark:text-white">{permData?.team_max[key as keyof typeof permData.team_max]?.title || key}</span>
                        <ToggleButton
                          isOn={!!toggles[key]}
                          onToggle={() => setToggles((prev) => ({ ...prev, [key]: !prev[key] }))}
                          thumbClassName={toggles[key] ? "!bg-purple dark:!bg-green" : "!bg-grey"}
                          checkedTrackClassName="!bg-purple/20 dark:!bg-green/20"
                        />
                      </div>
                    ))}
                  </div>
                  {permsDirty && (
                    <Button className="btn-primary-medium w-fit" onClick={handleSavePerms} disabled={savingPerm}>
                      {savingPerm ? t("account:member_modal.saving") : t("account:member_modal.save_permissions")}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {onRemove && (
            <button
              type="button"
              className="self-start text-bodySm text-orange border border-orange/40 hover:bg-orange/10 rounded-md px-3 py-2 transition-colors"
              onClick={() => {
                handleClose();
                onRemove();
              }}
            >
              {t("account:member_modal.remove_from_team")}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(drawer, document.body);
};

export default ManageMemberModal;
